import { PRESENTATION_ROLES } from './components/WebApp/presentationAccess';
import { TerminologyProvider, useTerminology } from './terminology/TerminologyContext';
import TerminologySettings from './components/WebManager/pages/TerminologySettings';
import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';

import { APP_BRAND, USER_ROLES, FULL_ROUTES, PAGE_TITLE_MAP, EXACT_PAGE_TITLES } from './config/Routesconfig'

import Login          from './components/WebManager/Auth/Login';
import Register       from './components/WebManager/Auth/Register';
import Unauthorized   from './components/WebManager/Auth/Unauthorized';
import Layout         from './components/Layout';
import PrivateRoute   from './components/WebManager/Auth/PrivateRoute';
import WebAppLogin    from './components/WebApp/WebAppLogin';
import WebAppJoin     from './components/WebApp/WebAppJoin';
import SignIn         from './components/WebApp/SignIn';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

import SuperadminDashboard  from './components/WebManager/Dashboard/SuperadminDashboard';
import AddReseller          from './components/WebManager/scenes/Reseller/Add&Edit/AddReseller';
import EditReseller         from './components/WebManager/scenes/Reseller/Add&Edit/EditReseller';
import ResellerDashboard    from './components/WebManager/Dashboard/ResellerDashboard';
import AddClient            from './components/WebManager/scenes/ClientAdmin/AddClient';
import EditClient           from './components/WebManager/scenes/ClientAdmin/EditClient';
import AddEnterpriseClient  from './components/WebManager/scenes/EnterpriseClient/AddEnterpriseClient';
import EditEnterpriseClient from './components/WebManager/scenes/EnterpriseClient/EditEnterpriseClient';
import Clients              from './components/WebManager/pages/Clients';
import EnterpriseClients    from './components/WebManager/pages/EnterpriseClients';
import Reports              from './components/WebManager/pages/Reports';
import Account              from './components/WebManager/pages/Account';
import Notify               from './components/WebManager/pages/Notify';
import Admins               from './components/WebManager/pages/Admins';
import Session              from './components/WebApp/Session';
import JoinSession          from './components/WebApp/JoinSession';
import ClientUserDashboard  from './components/WebManager/Dashboard/ClientUserDashboard';
import ClientDashboard      from './components/WebManager/Dashboard/ClientDashboard';
import AdminDashboard       from './components/WebManager/Dashboard/AdminDashboard';
import AddCaseForm          from './components/WebManager/scenes/CaseManagement/AddCase/AddCases';
import ViewCase             from './components/WebManager/scenes/CaseManagement/ViewCase';
import { ThemeProvider }    from './components/ThemeContext';
import ThemePage            from './components/WebManager/pages/ThemePage';
import CaseManagement       from './components/WebManager/pages/CaseManagement';
import SampleViewCase       from './components/WebManager/scenes/CaseManagement/SampleCase/SampleCase';
import AddArbitration       from './components/WebManager/scenes/Sessions/Arbitration';
import AddDeposition        from './components/WebManager/scenes/Sessions/Deposition';
import AddTrail             from './components/WebManager/scenes/Sessions/Trail';
import AddMediation         from './components/WebManager/scenes/Sessions/Mediation';
import AddWitnessPrep       from './components/WebManager/scenes/Sessions/WinessPrep';
import UserManagement       from './components/WebManager/pages/UserManagement';
import AddUsers             from './components/WebManager/scenes/ClientUser/Addusers';
import EditUsers            from './components/WebManager/scenes/ClientUser/EditUsers';
import EmailLogs            from './components/WebManager/pages/EmailLogs';
import ResellerHierarchy    from './components/WebManager/scenes/Reseller/ResellerHierarchy';
import ViewDeposition       from './components/WebManager/scenes/CaseManagement/Depositions/ViewDeposition';
import EditDeposition       from './components/WebManager/scenes/CaseManagement/Depositions/EditDeposition';
import AddAdmin             from './components/WebManager/scenes/Admin/AddAdmin';
import EditAdmin            from './components/WebManager/scenes/Admin/EditAdmin';
import AddCustomSession     from './components/WebManager/scenes/Sessions/Custom';
import UserReport           from './components/WebManager/scenes/Reports/UserReport';
import CommissionReports    from './components/WebManager/scenes/Reports/CommissionReports';
import UsageReports         from './components/WebManager/scenes/Reports/UsageReports';
import FinancialReport      from './components/WebManager/scenes/Reports/FinancialReport';
import Resellers            from './components/WebManager/pages/Resellers';
import Presentation         from './components/WebApp/Presentation';
import ForgotPassword from "./components/WebManager/Auth/ForgotPassword";
import ResetPassword  from "./components/WebManager/Auth/ResetPassword";
import ForgotUsername from './components/WebManager/Auth/ForgotUsername';
import SetPassword from './components/WebManager/pages/SetPassword';

// ───────────────
// Route group aliases
// ───────────────
const superadminRoutes         = FULL_ROUTES.superadminRoutes;
const resellerRoutes           = FULL_ROUTES.resellerRoutes;
const clientRoutes             = FULL_ROUTES.clientRoutes;
const adminRoutes              = FULL_ROUTES.adminRoutes;
const superadminAbsoluteRoutes = FULL_ROUTES.superadminAbsoluteRoutes;
const adminAbsoluteRoutes      = FULL_ROUTES.adminAbsoluteRoutes;

// ───────────────
// TitleManager
// ───────────────
const TitleManager = () => {
  const location = useLocation();
  const { labels } = useTerminology();

  useEffect(() => {
    const currentPath = (location?.pathname || '/').toLowerCase();

    if (currentPath === FULL_ROUTES.terminology) {
      document.title = 'Terminology Settings';
      return;
    }

    if (EXACT_PAGE_TITLES[currentPath]) {
      document.title = EXACT_PAGE_TITLES[currentPath];
      return;
    }

    const pathSegments = currentPath.split('/').filter(Boolean);
    for (const { segments, title } of PAGE_TITLE_MAP) {
      for (const seg of pathSegments) {
        if (segments.includes(seg)) {
          document.title = title;
          return;
        }
      }
    }

    if (pathSegments.length > 0) {
      const lastSegment = pathSegments[pathSegments.length - 1].replace(/-/g, ' ');
      document.title = lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1);
      return;
    }

    document.title = APP_BRAND.appName;
  }, [location, labels]);

  return null;
};

// ───────────────
// Role sets
// ───────────────
const ROLE_SETS = {
  allManagers:   [USER_ROLES.superadmin, USER_ROLES.admin, USER_ROLES.reseller, USER_ROLES.clientAdmin, USER_ROLES.enterprise],
  reportViewers: [USER_ROLES.superadmin, USER_ROLES.admin, USER_ROLES.reseller],
  clientUsers:   [USER_ROLES.clientAdmin, USER_ROLES.enterprise, USER_ROLES.superadmin, USER_ROLES.reseller, USER_ROLES.admin, USER_ROLES.clientUser],
};

// ───────────────
// App
// ───────────────
const App = () => (
  <TerminologyProvider>
  <ThemeProvider>
    <Router>
      <TitleManager />

      <Routes>
        <Route path={FULL_ROUTES.terminology} element={
          <PrivateRoute allowedRoles={[USER_ROLES.superadmin]}>
            <Layout><TerminologySettings /></Layout>
          </PrivateRoute>
        } />

        {/* ── Public / auth ─── */}
        <Route path={FULL_ROUTES.login}       element={<Login />} />
        <Route path={FULL_ROUTES.register}    element={<Register />} />
        <Route path={FULL_ROUTES.signin}      element={<Layout><SignIn /></Layout>} />
        <Route path={FULL_ROUTES.member}      element={<Layout><WebAppLogin showForm={true} /></Layout>} />
        <Route path={FULL_ROUTES.nonMembers}  element={<Layout><WebAppJoin showForms={true} /></Layout>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password"  element={<ResetPassword />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password"  element={<ResetPassword />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path={FULL_ROUTES.session}     element={<Session />} />
        <Route path={FULL_ROUTES.sessionId}   element={<div>Session Page (Placeholder)</div>} />
        <Route path={FULL_ROUTES.joinSession} element={<JoinSession />} />

        <Route
          path={FULL_ROUTES.zoomConnected}
          element={
            <PrivateRoute allowedRoles={[USER_ROLES.clientAdmin]}>
              <Navigate to={FULL_ROUTES.session} replace />
            </PrivateRoute>
          }
        />

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* SUPERADMIN                                                       */}
        {/* ════════════════════════════════════════════════════════════════ */}
        <Route
          path={FULL_ROUTES.superadmin}
          element={
            <PrivateRoute allowedRoles={[USER_ROLES.superadmin]}>
              <Layout><SuperadminDashboard /></Layout>
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to={superadminRoutes.defaultRedirect} replace />} />

          {/* ── Resellers / Advisors ── */}
          <Route path={superadminRoutes.resellers}            element={<Resellers />} />
          <Route path={superadminRoutes.addReseller}          element={<AddReseller />} />
          <Route path={superadminRoutes.editReseller}         element={<EditReseller />} />
          <Route path={superadminRoutes.resellerHierarchy}    element={<ResellerHierarchy />} />

          {/* ── Admins ── */}
          <Route path={superadminRoutes.admins}               element={<Admins />} />
          <Route path={superadminRoutes.addAdmin}             element={<AddAdmin />} />
          <Route path={superadminRoutes.editAdmin}            element={<EditAdmin />} />

          {/* ── Clients ── */}
          <Route path={superadminRoutes.clients}              element={<Clients />} />
          <Route path={superadminRoutes.addClient}            element={<AddClient />} />
          <Route path={superadminRoutes.editClient}           element={<EditClient />} />

          {/* ── Enterprise Clients ── */}
          <Route path={superadminRoutes.enterpriseClients}    element={<EnterpriseClients />} />
          <Route path={superadminRoutes.addEnterpriseClient}  element={<AddEnterpriseClient />} />
          <Route path={superadminRoutes.editEnterpriseClient} element={<EditEnterpriseClient />} />

          {/* ── Other pages ── */}
          <Route path={superadminRoutes.reports}              element={<Reports />} />
          <Route path={superadminRoutes.account}              element={<Account />} />
          <Route path={superadminRoutes.notify}               element={<Notify />} />
          <Route path={superadminRoutes.notifyLogs}           element={<EmailLogs />} />
        </Route>

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* ADMIN (nested)                                                   */}
        {/* ════════════════════════════════════════════════════════════════ */}
        <Route
          path={FULL_ROUTES.admin}
          element={
            <PrivateRoute allowedRoles={[USER_ROLES.admin]}>
              <Layout><AdminDashboard /></Layout>
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to={adminRoutes.defaultRedirect} replace />} />
          <Route path={adminRoutes.dashboard}            element={<AdminDashboard />} />

          {/* ── Resellers / Advisors ── */}
          <Route path={adminRoutes.resellers}            element={<Resellers />} />

          {/* ── Clients ── */}
          <Route path={adminRoutes.clients}              element={<Clients />} />
          <Route path={adminRoutes.addClient}            element={<AddClient />} />
          <Route path={adminRoutes.editClient}           element={<EditClient />} />

          {/* ── Enterprise Clients ── */}
          <Route path={adminRoutes.enterpriseClients}    element={<EnterpriseClients />} />
          <Route path={adminRoutes.editEnterpriseClient} element={<EditEnterpriseClient />} />

          {/* ── Other pages ── */}
          <Route path={adminRoutes.admins}               element={<AdminDashboard />} />
          <Route path={adminRoutes.account}              element={<Account />} />
          <Route path={adminRoutes.notify}               element={<AdminDashboard />} />
          <Route path={adminRoutes.reports}              element={<AdminDashboard />} />
        </Route>

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* REPORTS (top-level, fixed paths)                                 */}
        {/* ════════════════════════════════════════════════════════════════ */}
        <Route path={FULL_ROUTES.userReport}
          element={<PrivateRoute allowedRoles={ROLE_SETS.reportViewers}><Layout><UserReport /></Layout></PrivateRoute>} />
        <Route path={FULL_ROUTES.commissionReport}
          element={<PrivateRoute allowedRoles={ROLE_SETS.reportViewers}><Layout><CommissionReports /></Layout></PrivateRoute>} />
        <Route path={FULL_ROUTES.usageReport}
          element={<PrivateRoute allowedRoles={ROLE_SETS.reportViewers}><Layout><UsageReports /></Layout></PrivateRoute>} />
        <Route path={FULL_ROUTES.financialReport}
          element={<PrivateRoute allowedRoles={ROLE_SETS.reportViewers}><Layout><FinancialReport /></Layout></PrivateRoute>} />

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* SUPERADMIN ABSOLUTE ROUTES                                       */}
        {/* ════════════════════════════════════════════════════════════════ */}
        <Route
          path={superadminAbsoluteRoutes.addClientForReseller}
          element={
            <PrivateRoute allowedRoles={[USER_ROLES.superadmin]}>
              <Layout><AddClient /></Layout>
            </PrivateRoute>
          }
        />

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* ADMIN ABSOLUTE ROUTES                                            */}
        {/* ════════════════════════════════════════════════════════════════ */}
        <Route
          path={adminAbsoluteRoutes.addClientForReseller}
          element={
            <PrivateRoute allowedRoles={[USER_ROLES.admin]}>
              <Layout><AddClient /></Layout>
            </PrivateRoute>
          }
        />

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* PRESENTATION                                                     */}
        {/* ════════════════════════════════════════════════════════════════ */}
        <Route
          path={FULL_ROUTES.presentation}
          element={
            <PrivateRoute allowedRoles={PRESENTATION_ROLES}>
              <Presentation />
            </PrivateRoute>
          }
        />

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* THEME — /:username/theme (still a wildcard, must stay late)      */}
        {/* ════════════════════════════════════════════════════════════════ */}
        <Route
          path={FULL_ROUTES.theme}
          element={
            <PrivateRoute allowedRoles={ROLE_SETS.allManagers}>
              <Layout><ThemePage /></Layout>
            </PrivateRoute>
          }
        />

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* CLIENT / ENTERPRISE / DASHBOARD  (:username/dashboard)          */}
        {/* Must come BEFORE /:username so /user/dashboard hits this first. */}
        {/* ════════════════════════════════════════════════════════════════ */}
        <Route
          path={FULL_ROUTES.clientAdmin}
          element={
            <PrivateRoute allowedRoles={ROLE_SETS.clientUsers}>
              <Layout><ClientDashboard /></Layout>
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to={clientRoutes.defaultRedirect} replace />} />
          <Route path={clientRoutes.cases}             element={<CaseManagement />} />
          <Route path={clientRoutes.users}             element={<UserManagement />} />
          <Route path={clientRoutes.account}           element={<Account />} />
          <Route path={clientRoutes.sampleCase}        element={<SampleViewCase />} />
          <Route path={clientRoutes.sampleCaseView}    element={<ViewCase />} />   {/* ← ADD */}
          <Route path={clientRoutes.viewCase}          element={<ViewCase />} />
          <Route path={clientRoutes.sampleCase}        element={<SampleViewCase />} />
          <Route path={clientRoutes.addCase}           element={<AddCaseForm />} />
          <Route path={clientRoutes.editCase}          element={<AddCaseForm isEdit={true} />} />
          <Route path={clientRoutes.viewCase}          element={<ViewCase />} />
          <Route path={clientRoutes.addUser}           element={<AddUsers />} />
          <Route path={clientRoutes.editUser}          element={<EditUsers />} />
          <Route path={clientRoutes.addArbitration}    element={<AddArbitration />} />
          <Route path={clientRoutes.addDeposition}     element={<AddDeposition />} />
          <Route path={clientRoutes.addTrial}          element={<AddTrail />} />
          <Route path={clientRoutes.addMediation}      element={<AddMediation />} />
          <Route path={clientRoutes.addWitnessPrep}    element={<AddWitnessPrep />} />
          <Route path={clientRoutes.addCustomSession}  element={<AddCustomSession />} />
          <Route path={clientRoutes.editCustomSession} element={<AddCustomSession />} />
          <Route path={clientRoutes.viewSession}       element={<ViewDeposition />} />
          <Route path={clientRoutes.editDeposition}    element={<EditDeposition />} />
          <Route path={clientRoutes.editTrial}         element={<AddTrail isEdit={true} />} />
          <Route path={clientRoutes.editArbitration}   element={<AddArbitration />} />
          <Route path={clientRoutes.editMediation}     element={<AddMediation isEdit={true} />} />
          <Route path={clientRoutes.editWitnessPrep}   element={<AddWitnessPrep isEdit={true} />} />
          <Route path={clientRoutes.joinSession}       element={<JoinSession />} />
        </Route>
        /* Add this near the bottom of your Routes, but BEFORE /:username (reseller) route */

{/* ════════════════════════════════════════════════════════════════ */}
{/* CLIENT USER DASHBOARD  —  /:username/user-dashboard               */}
{/* ════════════════════════════════════════════════════════════════ */}
<Route
  path={FULL_ROUTES.clientUser}   // e.g., "/:username/user-dashboard"
  element={
    <PrivateRoute allowedRoles={[USER_ROLES.clientUser]}>
      <Layout><ClientUserDashboard /></Layout>
    </PrivateRoute>
  }
>
  <Route index element={<Navigate to={clientRoutes.cases} replace />} />

  {/* Main Cases Listing */}
  <Route path={clientRoutes.cases} element={<CaseManagement />} />
  <Route path={clientRoutes.viewCase}          element={<ViewCase />} />

  {/* Account */}
  <Route path={clientRoutes.account} element={<Account />} />

</Route>

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* RESELLER  /:username  — MUST BE LAST wildcard route             */}
        {/* Any named path not matched above falls here. Keep it at bottom. */}
        {/* ════════════════════════════════════════════════════════════════ */}
        <Route
          path={FULL_ROUTES.reseller}
          element={
            <PrivateRoute allowedRoles={[USER_ROLES.reseller]}>
              <Layout><ResellerDashboard /></Layout>
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to={resellerRoutes.defaultRedirect} replace />} />
          <Route path={resellerRoutes.clients}              element={<Clients />} />
          <Route path={resellerRoutes.enterpriseClients}    element={<EnterpriseClients />} />
          <Route path={resellerRoutes.reports}              element={<Reports />} />
          <Route path={resellerRoutes.account}              element={<Account />} />
          <Route path={resellerRoutes.addClient}            element={<AddClient />} />
          <Route path={resellerRoutes.editClient}           element={<EditClient />} />
          <Route path={resellerRoutes.addEnterpriseClient}  element={<AddEnterpriseClient />} />
          <Route path={resellerRoutes.editEnterpriseClient} element={<EditEnterpriseClient />} />
        </Route>

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* SUPERADMIN ABSOLUTE ROUTES                                       */}
        {/*   ONLY routes that are NOT already nested above go here.      */}
        {/* Currently: only addClientForReseller is unique to this section. */}
        {/* ════════════════════════════════════════════════════════════════ */}
        <Route
          path={superadminAbsoluteRoutes.addClientForReseller}
          element={
            <PrivateRoute allowedRoles={[USER_ROLES.superadmin]}>
              <Layout><AddClient /></Layout>
            </PrivateRoute>
          }
        />

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* ADMIN ABSOLUTE ROUTES                                            */}
        {/*   ONLY routes that are NOT already nested above go here.      */}
        {/* Currently: only addClientForReseller is unique to this section. */}
        {/* ════════════════════════════════════════════════════════════════ */}
        <Route
          path={adminAbsoluteRoutes.addClientForReseller}
          element={
            <PrivateRoute allowedRoles={[USER_ROLES.admin]}>
              <Layout><AddClient /></Layout>
            </PrivateRoute>
          }
        />

        {/* ── Fallbacks ─── */}
        <Route path={FULL_ROUTES.home}         element={<Navigate to={FULL_ROUTES.login} replace />} />
        <Route path={FULL_ROUTES.unauthorized} element={<Unauthorized />} />
        <Route path="/"                        element={<Navigate to={FULL_ROUTES.login} replace />} />
        <Route path="*"                        element={<Navigate to={FULL_ROUTES.login} replace />} />
   

      </Routes>
    </Router>
  </ThemeProvider>
  </TerminologyProvider>
);

export default App;